import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../components/ui/InputField';
import TextAreaField from '../components/ui/TextAreaField';
import Button from '../components/ui/Button';

const RestoranReportForm = ({ onDataChange }) => {
  const { t } = useTranslation();
  const [restaurantInfo, setRestaurantInfo] = useState({
    restaurantName: '',
    reportDate: new Date().toISOString().split('T')[0],
    location: ''
  });

  const [salesItems, setSalesItems] = useState([
    { itemName: '', quantity: 1, unitPrice: 0, total: 0 }
  ]);

  const [expenses, setExpenses] = useState([
    { expenseItem: '', amount: 0 }
  ]);

  const [notes, setNotes] = useState('');

  useEffect(() => {
    const totalRevenue = salesItems.reduce((sum, item) => sum + item.total, 0);
    const totalExpenses = expenses.reduce((sum, item) => sum + item.amount, 0);
    const dailyProfit = totalRevenue - totalExpenses;

    onDataChange({
      ...restaurantInfo,
      salesItems,
      expenses,
      notes,
      totalRevenue,
      totalExpenses,
      dailyProfit,
      date: new Date().toLocaleDateString()
    });
  }, [restaurantInfo, salesItems, expenses, notes, onDataChange]);

  const handleSalesChange = (index, field, value) => {
    const newSales = [...salesItems];
    newSales[index][field] = value;
    
    if (field === 'quantity' || field === 'unitPrice') {
      newSales[index].total = newSales[index].quantity * newSales[index].unitPrice;
    }
    
    setSalesItems(newSales);
  };

  const handleExpenseChange = (index, field, value) => {
    const newExpenses = [...expenses];
    newExpenses[index][field] = value;
    setExpenses(newExpenses);
  };

  const totalRevenue = salesItems.reduce((sum, item) => sum + item.total, 0);
  const totalExpenses = expenses.reduce((sum, item) => sum + item.amount, 0);
  const dailyProfit = totalRevenue - totalExpenses;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 lg:p-8 border border-gray-200 dark:border-gray-700 shadow-sm space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <InputField
          label="Restaurant/Cafe Name"
          name="restaurantName"
          value={restaurantInfo.restaurantName}
          onChange={(e) => setRestaurantInfo({ ...restaurantInfo, restaurantName: e.target.value })}
          placeholder="e.g., Savanna Cafe"
          required
        />
        <InputField
          label="Report Date"
          name="reportDate"
          type="date"
          value={restaurantInfo.reportDate}
          onChange={(e) => setRestaurantInfo({ ...restaurantInfo, reportDate: e.target.value })}
        />
        <InputField
          label="Location"
          name="location"
          value={restaurantInfo.location}
          onChange={(e) => setRestaurantInfo({ ...restaurantInfo, location: e.target.value })}
          placeholder="Branch location"
        />
      </div>

      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Daily Sales</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Item Name</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">{t('form.quantity')}</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">{t('form.unitPrice')}</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">{t('form.total')}</th>
                <th className="p-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {salesItems.map((item, index) => (
                <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-2">
                    <input
                      className="form-input w-full bg-transparent border-0 focus:ring-0 p-2"
                      type="text"
                      value={item.itemName}
                      onChange={(e) => handleSalesChange(index, 'itemName', e.target.value)}
                      placeholder="e.g., Cappuccino, Pizza"
                    />
                  </td>
                  <td className="p-2">
                    <input
                      className="form-input w-20 bg-transparent border-0 focus:ring-0 p-2 text-center"
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleSalesChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      className="form-input w-28 bg-transparent border-0 focus:ring-0 p-2 text-right"
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => handleSalesChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td className="p-2 text-right font-medium text-[#111418] dark:text-gray-100">
                    ${item.total.toFixed(2)}
                  </td>
                  <td className="p-2 text-center">
                    <button onClick={() => setSalesItems(salesItems.filter((_, i) => i !== index))} className="text-gray-400 hover:text-red-500" disabled={salesItems.length === 1}>
                      <span className="material-symbols-outlined text-xl">delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={() => setSalesItems([...salesItems, { itemName: '', quantity: 1, unitPrice: 0, total: 0 }])} variant="ghost" icon="add_circle" className="mt-4">
            Add Item
          </Button>
        </div>
      </div>

      <div>
        <h3 className="text-xl font-bold text-[#111418] dark:text-white mb-4">Daily Expenses</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400">Expense Item</th>
                <th className="p-3 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">Amount</th>
                <th className="p-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense, index) => (
                <tr key={index} className="border-b border-gray-200 dark:border-gray-700">
                  <td className="p-2">
                    <input
                      className="form-input w-full bg-transparent border-0 focus:ring-0 p-2"
                      type="text"
                      value={expense.expenseItem}
                      onChange={(e) => handleExpenseChange(index, 'expenseItem', e.target.value)}
                      placeholder="e.g., Groceries, Utilities, Staff wages"
                    />
                  </td>
                  <td className="p-2">
                    <input
                      className="form-input w-full bg-transparent border-0 focus:ring-0 p-2 text-right"
                      type="number"
                      min="0"
                      step="0.01"
                      value={expense.amount}
                      onChange={(e) => handleExpenseChange(index, 'amount', parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td className="p-2 text-center">
                    <button onClick={() => setExpenses(expenses.filter((_, i) => i !== index))} className="text-gray-400 hover:text-red-500" disabled={expenses.length === 1}>
                      <span className="material-symbols-outlined text-xl">delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button onClick={() => setExpenses([...expenses, { expenseItem: '', amount: 0 }])} variant="ghost" icon="add_circle" className="mt-4">
            Add Expense
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <div className="w-full max-w-sm space-y-3 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-gray-400">Total Revenue</span>
            <span className="font-medium text-green-600 dark:text-green-400">${totalRevenue.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-gray-400">Total Expenses</span>
            <span className="font-medium text-red-600 dark:text-red-400">${totalExpenses.toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-lg font-bold text-[#111418] dark:text-gray-100">Daily Profit</span>
            <span className={`text-lg font-bold ${dailyProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${dailyProfit.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      <TextAreaField
        label={t('form.notes')}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Daily notes, special events, observations..."
        rows={3}
      />
    </div>
  );
};

export default RestoranReportForm;
